package com.islamicmedia.shared.data.storage

import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.DisplaySettings
import com.islamicmedia.shared.domain.model.MediaPref
import com.islamicmedia.shared.domain.model.ReferralBlockedAck
import com.islamicmedia.shared.domain.model.ReferralRedeemPromptSeen
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.presentation.HistoryItem
import kotlinx.browser.localStorage
import kotlin.js.console
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

actual class SettingsStorage {
    private val json = Json { ignoreUnknownKeys = true }

    // ── localStorage writes can THROW ─────────────────────────────────────────────────────────
    //
    // Web is the last platform still keeping the big blobs in the key-value store (iOS and Android
    // moved `analysis_history` / `titles_index` to files). It cannot do the same — there is no
    // filesystem, and IndexedDB is async while this whole expect/actual surface is synchronous.
    //
    // The acute problem is not size, it is that `localStorage.setItem` THROWS QuotaExceededError
    // when the origin quota (~5MB in most browsers) is full, and none of the ~20 call sites here
    // caught it. Real payloads measured on device were ~6.9MB of history — comfortably over. An
    // uncaught throw out of saveHistory() propagates into whatever coroutine called it.
    //
    // Worse, a full quota breaks EVERY write, so a too-large history would also stop search
    // criteria, display settings and coachmark flags from persisting — unrelated features quietly
    // failing because of one oversized value.
    //
    // So: every write goes through here, and the two big blobs evict themselves on failure (see
    // [setBlob]) rather than squatting on the quota.
    private fun setItemSafe(key: String, value: String): Boolean = try {
        localStorage.setItem(key, value)
        true
    } catch (e: Throwable) {
        // QuotaExceededError, or a browser with storage disabled entirely (Safari private mode
        // historically threw on any write). Never fatal — these are all caches or preferences,
        // and the server is authoritative for history.
        console.warn("[STORAGE] localStorage write failed for '$key' (${e.message}) — not persisted")
        false
    }

    /**
     * Write one of the large blobs, dropping it entirely if it will not fit.
     *
     * Leaving a stale multi-megabyte value behind would keep the quota full and keep every other
     * setting failing to save. Removing it costs only the instant-render cache: `loadRemoteHistory`
     * reloads the user's full history from the server on the next open, so nothing is lost that the
     * server does not already hold.
     *
     * Deliberately does NOT trim the list to make it fit — the remote sync loads the FULL history,
     * so a truncated local copy would just be silently wrong.
     */
    private fun setBlob(key: String, value: String) {
        if (!setItemSafe(key, value)) {
            try { localStorage.removeItem(key) } catch (_: Throwable) { }
            console.warn("[STORAGE] dropped '$key' from localStorage — it exceeds the quota; the server copy remains authoritative")
        }
    }

    actual fun saveSearchCriteria(criteria: SearchCriteria) {
        setItemSafe("search_criteria", json.encodeToString(criteria))
    }

    actual fun loadSearchCriteria(): SearchCriteria {
        val stored = localStorage.getItem("search_criteria")
        return if (stored != null) {
            try {
                json.decodeFromString<SearchCriteria>(stored)
            } catch (e: Exception) {
                SearchCriteria.DEFAULT
            }
        } else {
            SearchCriteria.DEFAULT
        }
    }

    actual fun saveDisplaySettings(settings: DisplaySettings) {
        setItemSafe("display_settings", json.encodeToString(settings))
    }

    actual fun loadDisplaySettings(): DisplaySettings {
        val stored = localStorage.getItem("display_settings")
        return if (stored != null) {
            try {
                json.decodeFromString<DisplaySettings>(stored)
            } catch (e: Exception) {
                DisplaySettings.DEFAULT
            }
        } else {
            DisplaySettings.DEFAULT
        }
    }

    actual fun saveHistory(history: List<HistoryItem>) {
        setBlob("analysis_history", json.encodeToString(history))
    }

    actual fun loadHistory(): List<HistoryItem> {
        val stored = localStorage.getItem("analysis_history")
        return if (stored != null) {
            try {
                json.decodeFromString<List<HistoryItem>>(stored)
            } catch (e: Exception) {
                emptyList()
            }
        } else {
            emptyList()
        }
    }

    actual fun saveMediaPrefs(mediaPrefs: List<MediaPref>) {
        setItemSafe("media_prefs", json.encodeToString(mediaPrefs))
    }

    actual fun loadMediaPrefs(): List<MediaPref> {
        val stored = localStorage.getItem("media_prefs") ?: return emptyList()
        return try { json.decodeFromString<List<MediaPref>>(stored) } catch (e: Exception) { emptyList() }
    }

    actual fun saveLastUserEmail(email: String) {
        setItemSafe("last_user_email", email)
    }

    actual fun loadLastUserEmail(): String? {
        return localStorage.getItem("last_user_email")
    }

    actual fun savePendingAnalysis(title: String, mediaType: String, year: String?, quickCheck: Boolean) {
        setItemSafe("pending_title", title)
        setItemSafe("pending_media_type", mediaType)
        if (year != null) setItemSafe("pending_year", year)
        else localStorage.removeItem("pending_year")
        setItemSafe("pending_quick", quickCheck.toString())
    }

    actual fun loadPendingAnalysis(): PendingAnalysis? {
        val title = localStorage.getItem("pending_title") ?: return null
        val mediaType = localStorage.getItem("pending_media_type") ?: return null
        val year = localStorage.getItem("pending_year")
        // Absent on records saved before the mode was persisted → null (resume uses the UI mode).
        val quickCheck = localStorage.getItem("pending_quick")?.toBooleanStrictOrNull()
        return PendingAnalysis(title, mediaType, year, quickCheck)
    }

    actual fun clearPendingAnalysis() {
        localStorage.removeItem("pending_title")
        localStorage.removeItem("pending_media_type")
        localStorage.removeItem("pending_year")
        localStorage.removeItem("pending_quick")
    }

    actual fun clearUserData() {
        localStorage.removeItem("analysis_history")
        localStorage.removeItem("media_prefs")
        localStorage.removeItem("last_user_email")
        localStorage.removeItem("pending_title")
        localStorage.removeItem("pending_media_type")
        localStorage.removeItem("pending_year")
        localStorage.removeItem("pending_quick")
        localStorage.removeItem("referral_cache")
    }

    actual fun saveDhikrHighScore(score: Int) {
        setItemSafe("dhikr_high_score", score.toString())
    }

    actual fun loadDhikrHighScore(): Int =
        localStorage.getItem("dhikr_high_score")?.toIntOrNull() ?: 0

    actual fun saveAnalysisCount(count: Int) {
        setItemSafe("analysis_count", count.toString())
    }

    actual fun loadAnalysisCount(): Int =
        localStorage.getItem("analysis_count")?.toIntOrNull() ?: 0

    actual fun saveReviewPromptCount(count: Int) {
        setItemSafe("review_prompt_count", count.toString())
    }

    actual fun loadReviewPromptCount(): Int =
        localStorage.getItem("review_prompt_count")?.toIntOrNull() ?: 0

    actual fun saveLastReviewPromptAt(count: Int) {
        setItemSafe("review_last_prompt_at_count", count.toString())
    }

    actual fun loadLastReviewPromptAt(): Int? =
        localStorage.getItem("review_last_prompt_at_count")?.toIntOrNull()

    actual fun saveHasSeenCoachmarks(seen: Boolean) {
        setItemSafe("has_seen_coachmarks", seen.toString())
    }

    actual fun loadHasSeenCoachmarks(): Boolean =
        localStorage.getItem("has_seen_coachmarks")?.toBoolean() ?: false

    actual fun saveHasSeenCreditsCoachmark(seen: Boolean) {
        setItemSafe("has_seen_credits_coachmark", seen.toString())
    }

    actual fun loadHasSeenCreditsCoachmark(): Boolean =
        localStorage.getItem("has_seen_credits_coachmark")?.toBoolean() ?: false

    actual fun saveHasSeenFiltersCoachmark(seen: Boolean) {
        setItemSafe("has_seen_filters_coachmark", seen.toString())
    }

    actual fun loadHasSeenFiltersCoachmark(): Boolean =
        localStorage.getItem("has_seen_filters_coachmark")?.toBoolean() ?: false

    actual fun saveHasSeenResultCoachmarks(seen: Boolean) {
        setItemSafe("has_seen_result_coachmarks", seen.toString())
    }

    actual fun loadHasSeenResultCoachmarks(): Boolean =
        localStorage.getItem("has_seen_result_coachmarks")?.toBoolean() ?: false

    actual fun saveTitlesIndex(items: List<DisambiguationItem>) {
        setBlob("titles_index", json.encodeToString(items))
    }

    actual fun loadTitlesIndex(): List<DisambiguationItem> {
        val stored = localStorage.getItem("titles_index") ?: return emptyList()
        return try { json.decodeFromString<List<DisambiguationItem>>(stored) } catch (e: Exception) { emptyList() }
    }

    actual fun saveRecentSearches(queries: List<String>) {
        setItemSafe("recent_searches", json.encodeToString(queries))
    }

    actual fun loadRecentSearches(): List<String> {
        val stored = localStorage.getItem("recent_searches") ?: return emptyList()
        return try { json.decodeFromString<List<String>>(stored) } catch (e: Exception) { emptyList() }
    }

    // Same localStorage key the web switcher (AppEntry/Index) already reads/writes.
    actual fun saveAnonId(id: String) {
        setItemSafe("funnel-anon-id", id)
    }

    actual fun loadAnonId(): String? = localStorage.getItem("funnel-anon-id")

    // Multi-word web keys in this file use hyphens where Android/iOS use underscores
    // ("funnel-anon-id" vs "funnel_anon_id"). Deliberately not normalised — changing an existing
    // key orphans live data — so new keys follow the local convention too.
    actual fun saveAttribution(json: String) {
        setItemSafe("attribution", json)
    }

    actual fun loadAttribution(): String? = localStorage.getItem("attribution")

    // Web attribution comes from document.referrer + the query string, not from Play — this pair
    // exists only to satisfy the expect.
    actual fun saveInstallReferrerCaptured(captured: Boolean) {
        setItemSafe("install-referrer-captured", captured.toString())
    }

    actual fun loadInstallReferrerCaptured(): Boolean =
        localStorage.getItem("install-referrer-captured")?.toBoolean() ?: false

    actual fun saveEnvOverride(value: String?) {
        if (value == null) localStorage.removeItem("env-override") else setItemSafe("env-override", value)
    }

    actual fun loadEnvOverride(): String? = localStorage.getItem("env-override")

    // Written by AppEntry's module-init capture of ?refcode= and read by both the app (after
    // sign-in) and the /register offer page, which all go through this one key. Wrapped in
    // try/catch because this runs at module init, where a throw (storage disabled) would take the
    // whole page down with it.
    actual fun savePendingReferralCode(code: String?, atMs: Long) {
        val encoded = PendingReferralCode.encode(code, atMs)
        if (encoded == null) {
            try { localStorage.removeItem(PENDING_REFERRAL_KEY) } catch (_: Throwable) { }
        } else {
            setItemSafe(PENDING_REFERRAL_KEY, encoded)
        }
    }

    actual fun loadPendingReferralCode(): String? = try {
        PendingReferralCode.decode(
            localStorage.getItem(PENDING_REFERRAL_KEY),
            com.islamicmedia.shared.util.currentTimeMillis(),
        )
    } catch (_: Throwable) { null }

    actual fun saveReferralCache(value: String?) {
        if (value == null) {
            try { localStorage.removeItem("referral_cache") } catch (_: Throwable) { }
        } else {
            setItemSafe("referral_cache", value)
        }
    }

    actual fun loadReferralCache(): String? = try { localStorage.getItem("referral_cache") } catch (_: Throwable) { null }

    actual fun hasSeenReferralRedeemPrompt(owner: String): Boolean = try {
        ReferralRedeemPromptSeen.hasSeen(localStorage.getItem(REDEEM_PROMPT_SEEN_KEY), owner)
    } catch (_: Throwable) { false }

    actual fun markReferralRedeemPromptSeen(owner: String) {
        try {
            val updated = ReferralRedeemPromptSeen.markSeen(localStorage.getItem(REDEEM_PROMPT_SEEN_KEY), owner)
            setItemSafe(REDEEM_PROMPT_SEEN_KEY, updated)
        } catch (_: Throwable) { }
    }

    actual fun hasAcknowledgedReferralBlocked(owner: String, blockedAt: String): Boolean = try {
        ReferralBlockedAck.hasAcknowledged(localStorage.getItem(BLOCKED_ACK_KEY), owner, blockedAt)
    } catch (_: Throwable) { false }

    actual fun markReferralBlockedAcknowledged(owner: String, blockedAt: String) {
        try {
            val updated = ReferralBlockedAck.acknowledge(localStorage.getItem(BLOCKED_ACK_KEY), owner, blockedAt)
            setItemSafe(BLOCKED_ACK_KEY, updated)
        } catch (_: Throwable) { }
    }

    private companion object {
        const val PENDING_REFERRAL_KEY = "pending-referral-code"
        const val REDEEM_PROMPT_SEEN_KEY = "referral-redeem-prompt-seen"
        const val BLOCKED_ACK_KEY = "referral-blocked-ack"
    }
}
